import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { CheckCircle, Lock, Trophy, Gift } from 'lucide-react';
import { MISSIONS } from '../data/missions';

const tileVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  show: { opacity: 1, scale: 1 }
};

const MissionsPage = () => {
  const { completedMissions, claimedMissions, claimMissionReward } = useGame();

  const completedCount = completedMissions.length;
  const totalCount = MISSIONS.length;
  const progressPercent = (completedCount / totalCount) * 100;

  return (
    <div className="relative h-full w-full bg-black flex flex-col overflow-hidden">

      {/* ===== CYBERPUNK ANIMATED BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {/* Neon Plasma Cloud */}
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]
          bg-[radial-gradient(circle,rgba(139,92,246,0.35),rgba(0,0,0,0.9)_55%)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 220, repeat: Infinity, ease: "linear" }}
        />

        {/* Cyan Energy Sweep */}
        <motion.div
          className="absolute inset-0
          bg-[linear-gradient(120deg,transparent,rgba(34,211,238,0.25),transparent)]"
          animate={{ x: ["-120%", "120%"] }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />

        {/* Holographic Grid Glow */}
        <motion.div
          className="absolute inset-0
          bg-[radial-gradient(circle_at_50%_80%,rgba(34,211,238,0.18),transparent_60%)]"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Vignette */}
        <div className="absolute inset-0 
          bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_40%,rgba(0,0,0,0.95)_100%)]" />
      </div>

      {/* ===== STICKY HUD ===== */}
      <div className="sticky top-0 z-20 bg-black/70 backdrop-blur-xl border-b border-cyan-400/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <h1 className="text-3xl font-black tracking-widest uppercase flex items-center gap-2 text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">
            <Trophy /> Achievements Board
          </h1>

          <div className="w-full md:w-1/3">
            <div className="flex justify-between text-xs text-cyan-300 mb-1 font-bold">
              <span>PROGRESS</span>
              <span>{completedCount}/{totalCount}</span>
            </div>
            <div className="h-3 rounded-full bg-black border border-cyan-400/40 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 1 }}
                className="h-full bg-gradient-to-r from-purple-500 via-cyan-400 to-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ===== MISSION GRID ===== */}
      <div className="flex-1 overflow-y-auto px-4 py-6 relative z-10">
        <motion.div
          initial="hidden"
          animate="show"
          className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5"
        >
          {MISSIONS.map((mission) => {
            const isCompleted = completedMissions.includes(mission.id);
            const isClaimed = claimedMissions.includes(mission.id);

            let frame = "border-cyan-400/10 bg-slate-900/80";
            let icon = <Lock size={18} className="text-slate-500" />;

            if (isClaimed) {
              frame = "border-emerald-400/40 bg-emerald-950/40 shadow-[0_0_20px_rgba(16,185,129,0.5)]";
              icon = <CheckCircle size={18} className="text-emerald-400" />;
            } else if (isCompleted) {
              frame = "border-purple-400/60 bg-gradient-to-b from-purple-900/40 to-black shadow-[0_0_25px_rgba(168,85,247,0.6)]";
              icon = <Gift size={18} className="text-purple-300 animate-pulse" />;
            }

            return (
              <motion.div
                key={mission.id}
                variants={tileVariants}
                whileHover={{ scale: 1.05 }}
                className={`relative rounded-xl border p-4 h-44 flex flex-col justify-between transition-all ${frame}`}
              >
                <div className="flex justify-between items-start">
                  <span className="text-[10px] font-black tracking-widest text-cyan-300/70">
                    MISSION {mission.id}
                  </span>
                  {icon}
                </div>

                <div>
                  <h3 className={`text-sm font-bold leading-tight ${isClaimed ? "text-emerald-400 line-through" : "text-white"}`}>
                    {mission.title}
                  </h3>
                  <p className="text-[10px] font-mono text-cyan-200/60 mt-1 line-clamp-2">
                    {mission.desc}
                  </p>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-mono text-purple-300">
                    {mission.reward}
                  </span>

                  {isCompleted && !isClaimed && (
                    <button
                      onClick={() => claimMissionReward(mission.id)}
                      className="bg-gradient-to-r from-purple-500 to-cyan-400 text-black text-[10px] font-black px-3 py-1 rounded shadow-[0_0_15px_rgba(34,211,238,0.8)]"
                    >
                      CLAIM
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default MissionsPage;
